/**
 * Display for legacy single-name grid columns (Enhancement 10 AC #27).
 * @param {string} primaryName
 * @param {number} totalCount
 */
export function formatRequestContactDisplay(primaryName, totalCount) {
  const name = String(primaryName ?? "").trim();
  if (!name) return "";
  const count = Number(totalCount) || 0;
  if (count <= 1) return name;
  return `${name} +${count - 1} more`;
}

/**
 * Details-page line matching VQA Vendor Contact: "Name | email".
 * @param {{ first_name?: string, last_name?: string, email?: string }} contact
 */
export function formatRequestContactNameEmail(contact) {
  const name = [contact?.first_name, contact?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const email = String(contact?.email ?? "").trim();
  if (name && email) return `${name} | ${email}`;
  return name || email || "";
}

/**
 * Contacts for VQQ/VQA details; falls back to legacy single email when list is empty.
 * @param {{ request_contacts?: Array<object>, vendor_contact_email?: string }} details
 */
export function getRequestContactsForDetails(details) {
  const contacts = details?.request_contacts;
  if (Array.isArray(contacts) && contacts.length > 0) {
    return [...contacts].sort(
      (a, b) => (Number(a?.sort_order) || 0) - (Number(b?.sort_order) || 0)
    );
  }
  const legacyEmail = String(details?.vendor_contact_email ?? "").trim();
  if (legacyEmail) return [{ email: legacyEmail }];
  return [];
}

/**
 * Resolve request-Primary on submit: vendor category Primary if still selected, else first chip.
 * @param {Array<object>} selectedContacts - ordered selection from multi-select
 * @param {'rfi'|'assessment'} category - CONTACT_CATEGORY.RFI | CONTACT_CATEGORY.ASSESSMENT
 */
export function resolveRequestPrimaryContact(selectedContacts, category) {
  if (!selectedContacts?.length) return null;
  const vendorPrimary = selectedContacts.find((c) => c.isCategoryPrimary);
  return vendorPrimary ?? selectedContacts[0];
}

/**
 * Person name for Hypercomply purchaser_vendor_contacts[].name: first+last from any
 * known contact shape, then display/name fields, then the typed form name.
 * @param {object|null} contact - selected contact (API snake_case or normalized camelCase)
 * @param {{firstName?: string, lastName?: string}} formFallback - typed form values
 */
export function resolveContactPersonName(contact, formFallback = {}) {
  return (
    [
      contact?.first_name ?? contact?.firstName,
      contact?.last_name ?? contact?.lastName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    contact?.displayName ||
    contact?.name ||
    [formFallback.firstName, formFallback.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    ""
  );
}

/**
 * Build API request_contacts / provider_contacts payload from multi-select chips.
 * @param {Array<object>} selectedContacts
 */
export function buildRequestContactsPayload(selectedContacts) {
  const primary = resolveRequestPrimaryContact(selectedContacts);
  return (selectedContacts ?? []).map((contact, index) => ({
    email: (contact.email ?? "").trim().toLowerCase(),
    first_name: contact.first_name ?? contact.firstName ?? "",
    last_name: contact.last_name ?? contact.lastName ?? "",
    job_title: contact.job_title ?? contact.title ?? contact.displayTitle ?? "",
    is_request_primary: contact.id === primary?.id,
    is_ad_hoc: Boolean(contact.isAdHoc ?? contact.is_ad_hoc),
    sort_order: index,
    is_vendor_category_primary: Boolean(contact.isCategoryPrimary),
  }));
}

/**
 * Extract email list for VQQ HyperComply invite (all selected chips).
 * @param {Array<object>} selectedContacts
 */
export function buildVqqContactEmails(selectedContacts) {
  return (selectedContacts ?? [])
    .map((c) => c.email?.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Grid display from API row (supports new and legacy shapes).
 * @param {object} item
 */
export function getRequestContactDisplayFromApiItem(item) {
  if (item?.vendor_contact_display) return item.vendor_contact_display;
  if (item?.request_contact_display) return item.request_contact_display;
  if (item?.provider_contact_display) return item.provider_contact_display;

  const contacts = item?.request_contacts ?? item?.provider_contacts;
  if (Array.isArray(contacts) && contacts.length > 0) {
    const primary =
      contacts.find((c) => c.is_request_primary) ?? contacts[0];
    const name = [primary.first_name, primary.last_name].filter(Boolean).join(" ").trim()
      || primary.email
      || "";
    return formatRequestContactDisplay(name, contacts.length);
  }

  const legacyName =
    item?.provider_contact_name ??
    item?.vendor_contact_name ??
    item?.vendorContactName ??
    "";
  const legacyEmail = item?.vendor_contact_email ?? item?.provider_contact_email ?? "";
  if (legacyName) return String(legacyName).trim();
  if (legacyEmail) return String(legacyEmail).trim();
  return "";
}

/** @returns {string|null} Error message when selection is invalid. */
export function validateRequestContactSelection(selectedContacts, hasEligibleContacts) {
  if ((selectedContacts?.length ?? 0) > 0) return null;
  return hasEligibleContacts
    ? "Select at least one request contact."
    : "Add at least one request contact.";
}
